import { useContext } from "react"
import { OfferContext } from "../offer-context";
import { ApiResponseV2, QuoteState } from "@vertical-insure/shared";

const offerQuoteStatus = (hideOnError: boolean, quoteState: QuoteState) => {
  const allQuotes = Object.values(quoteState).flat();

  const hasValidQuote = allQuotes.some(response => {
    if ('decisions' in response) {
      const decisions = (response as ApiResponseV2).decisions;
      return decisions.some(decision => decision.options.some(o => o.data !== undefined))
    }

    return ('quote_id' in response);
  });

  return {
    shouldDisplayLayout: hasValidQuote || (!hasValidQuote && !hideOnError),
    hasValidQuote,
  }
}

export const getSelectedQuotes = (quotes: QuoteState, acceptedQuoteIds: string[]) => {
  if (!acceptedQuoteIds) {
    return [];
  }

  return Object.fromEntries(
    Object.entries(quotes).map(([productKey, productQuotes]) => [
      productKey,
      productQuotes.flatMap(quote => {
        if ('quote_id' in quote) {
          return acceptedQuoteIds.includes(quote.quote_id) ? [quote] : [];
        }
        if ('decisions' in quote) {
          return quote.decisions
            .flatMap(decision => 
              decision.options
                .filter(option => option.data && acceptedQuoteIds.includes(option.data.quote_id))
                .map(option => option.data)
            );
        }
        return [];
      })
    ])
  );
}

/**
 * Finds the first quote in the quotes object 
 * used by payment components to know currency or whether to render payment elements
 * @param quotes The quotes object to search through.
 * @param acceptedQuotes Array of accepted quote IDs
 * @returns The first quote found in the quotes object, prioritizing accepted decisions if they exist.
 */
const findFirstQuote = (quotes: QuoteState, acceptedQuotes: string[]) => {
  for (const productQuotes of Object.values(quotes)) {
    for (const quote of productQuotes) {
      if ('quote_id' in quote) {
        return quote;
      }
      if ('decisions' in quote) {
        // First try to find a decision with an accepted option
        for (const decision of quote.decisions) {
          for (const option of decision.options) {
            if (option.data && 'quote_id' in option.data && acceptedQuotes.includes(option.data.quote_id)) {
              return option.data;
            }
          }
        }
        // If no accepted option found, fall back to first available option
        for (const decision of quote.decisions) {
          for (const option of decision.options) {
            if (option.data && 'quote_id' in option.data) {
              return option.data;
            }
          }
        }
      }
    }
  }
  return null;
};

export const useOfferContext = () => {
  const offerContext = useContext(OfferContext);
  const hideOnError = offerContext.config?.hide_on_error === undefined
    ? true : offerContext.config?.hide_on_error;
  const { shouldDisplayLayout, hasValidQuote } = offerQuoteStatus(hideOnError, offerContext.quotes);
  return {
    ...offerContext,
    shouldDisplayLayout,
    hasValidQuote,
    getSelectedQuotes: (acceptedQuoteIds: string[]) =>
      getSelectedQuotes(offerContext.quotes, acceptedQuoteIds),
    findFirstQuote: () => findFirstQuote(offerContext.quotes, offerContext.acceptedQuotes)
  };
}
